import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Navbar from '../components/Navbar';
import AdminTabs from '../components/AdminTabs';
import AdminLoadingScreen from '../components/AdminLoadingScreen';
import {
  Scale,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  MessageSquare,
  User,
  RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function useDateFormatter() {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';
  return (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };
}

function useMoneyFormatter() {
  // Los reembolsos se emiten en DOP (RD$) como el resto del sistema (Azul).
  // Antes usaba USD, lo que mostraba montos incorrectos en el panel del admin
  // y en los correos de resolución de disputa.
  return (val) => `RD$${(val ?? 0).toLocaleString('es-DO')}`;
}

function StatusBadge({ status }) {
  const { t } = useTranslation();
  if (status === 'open') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <AlertCircle size={11} />
        {t('admin.open')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
      <CheckCircle size={11} />
      {t('admin.resolved')}
    </span>
  );
}

export default function DisputeManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fmt = useDateFormatter();
  const money = useMoneyFormatter();

  const [disputes, setDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [resolution, setResolution] = useState({
    type: '',
    refund_amount: 0,
    notes: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchDisputes = useCallback(
    async (filter) => {
      setLoading(true);
      setError('');
      try {
        const qs = filter !== 'all' ? `?status=${filter}` : '';
        const res = await fetch(`${BACKEND_URL}/api/admin/disputes${qs}`, {
          credentials: 'include',
        });
        if (res.status === 401 || res.status === 403) {
          navigate('/login');
          return;
        }
        if (!res.ok) throw new Error('Error');
        const data = await res.json();
        setDisputes(data);
        if (selectedDispute) {
          const refreshed = data.find((d) => d.id === selectedDispute.id);
          setSelectedDispute(refreshed || null);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  useEffect(() => {
    fetchDisputes(statusFilter);
  }, [statusFilter, fetchDisputes]);

  const handleSelect = (d) => {
    setSelectedDispute(d);
    setResolution({ type: '', refund_amount: d.total_price ?? 0, notes: '' });
    setSuccess('');
    setError('');
  };

  const handleResolve = async () => {
    if (!resolution.type) return;
    setResolving(true);
    setError('');
    setSuccess('');
    try {
      const body = {
        resolution_type: resolution.type,
        refund_amount:
          resolution.type === 'client'
            ? selectedDispute.total_price
            : resolution.type === 'partial'
            ? resolution.refund_amount
            : 0,
        notes: resolution.notes,
      };
      const res = await fetch(
        `${BACKEND_URL}/api/admin/disputes/${selectedDispute.id}/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('admin.errorResolving'));
      }
      setSuccess(t('admin.disputeResolvedOk'));
      await fetchDisputes(statusFilter);
    } catch (e) {
      // Antes: emoji ❌ hardcoded en el string, inconsistente con el resto del
      // panel admin que usa iconos lucide-react o ningún icono. El contenedor
      // rojo (red-50/red-700) ya comunica visualmente que es un error.
      setError(e.message);
    } finally {
      setResolving(false);
    }
  };

  const TABS = [
    { key: 'all', label: t('admin.tabAll') },
    { key: 'open', label: t('admin.tabOpen') },
    { key: 'resolved', label: t('admin.tabResolved') },
  ];

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Scale size={48} className="text-gray-200 mb-4" />
      <p className="text-gray-400 font-medium">{t('admin.noDisputesCat')}</p>
    </div>
  );

  const DisputeCard = ({ d }) => {
    const isActive = selectedDispute?.id === d.id;
    return (
      <button
        onClick={() => handleSelect(d)}
        className={`w-full text-left px-4 py-4 rounded-2xl border transition-all mb-2 ${
          isActive
            ? 'border-[#134168] bg-[#134168]/5'
            : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
        }`}
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className="text-sm font-bold text-gray-800 truncate">
            {d.booking_code || d.id}
          </span>
          <StatusBadge status={d.status} />
        </div>
        <p className="text-xs text-gray-500 mb-1 truncate">{d.client_name || '—'}</p>
        <p className="text-xs text-gray-400 line-clamp-2">{d.reason}</p>
        <p className="text-xs text-gray-300 mt-1.5">{fmt(d.created_at)}</p>
      </button>
    );
  };

  const ResolutionSection = ({ d }) => {
    const RADIO_OPTIONS = [
      { value: 'client', label: t('admin.favorClient') },
      { value: 'provider', label: t('admin.favorProvider') },
      { value: 'partial', label: t('admin.partialRes') },
    ];

    return (
      <div className="mt-6 border-t border-gray-100 pt-6">
        <h4 className="font-bold text-gray-800 mb-4">{t('admin.resolution')}</h4>

        <div className="space-y-3 mb-5">
          {RADIO_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 cursor-pointer px-4 py-3 rounded-xl border transition-colors ${
                resolution.type === opt.value
                  ? 'border-[#134168] bg-[#134168]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="resolution_type"
                value={opt.value}
                checked={resolution.type === opt.value}
                onChange={() =>
                  setResolution((r) => ({
                    ...r,
                    type: opt.value,
                    refund_amount: opt.value === 'client' ? d.total_price : r.refund_amount,
                  }))
                }
                className="accent-[#134168]"
              />
              <span className="text-sm font-medium text-gray-700">{opt.label}</span>
            </label>
          ))}
        </div>

        {resolution.type === 'client' && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4 text-sm text-blue-700">
            <DollarSign size={15} />
            <span><Trans i18nKey="admin.refundAmount100" values={{ amount: money(d.total_price) }} /></span>
          </div>
        )}
        {resolution.type === 'provider' && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-4 text-sm text-emerald-700">
            <DollarSign size={15} />
            <span>{t('admin.willReleaseFunds')}</span>
          </div>
        )}
        {resolution.type === 'partial' && (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              {t('admin.refundAmountUsd')}
            </label>
            <input
              type="number"
              min="0"
              max={d.total_price}
              step="0.01"
              value={resolution.refund_amount}
              onChange={(e) =>
                setResolution((r) => ({ ...r, refund_amount: parseFloat(e.target.value) }))
              }
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#134168]"
            />
          </div>
        )}

        <div className="mb-5">
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            {t('admin.resolutionNotes')}
          </label>
          <textarea
            rows={3}
            value={resolution.notes}
            onChange={(e) => setResolution((r) => ({ ...r, notes: e.target.value }))}
            placeholder={t('admin.resolutionNotesPh')}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#134168] resize-none"
          />
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
            {error}
          </div>
        )}
        {success && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-4">
            {success}
          </div>
        )}

        <button
          onClick={handleResolve}
          disabled={!resolution.type || resolving}
          className="w-full flex items-center justify-center gap-2 bg-[#134168] text-white py-3 rounded-full font-semibold hover:bg-[#39b4aa] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {resolving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {t('admin.processing')}
            </>
          ) : (
            <>
              <Scale size={16} />
              {t('admin.confirmResolution')}
            </>
          )}
        </button>
      </div>
    );
  };

  const ResolvedSummary = ({ d }) => (
    <div className="mt-6 border-t border-gray-100 pt-6">
      <h4 className="font-bold text-gray-800 mb-3">{t('admin.appliedResolution')}</h4>
      <div className="bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4 text-sm text-gray-700">
        <p>
          <span className="font-semibold">{t('admin.notesLabel')}</span>{' '}
          {d.resolution || t('admin.noNotes')}
        </p>
      </div>
    </div>
  );

  function InfoRow({ icon, label, value, multiline = false }) {
    return (
      <div className="flex gap-3 items-start">
        <div className="w-6 shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
            {label}
          </p>
          {multiline ? (
            <p className="text-sm text-gray-800 leading-relaxed">{value}</p>
          ) : (
            <div className="text-sm text-gray-800">{value}</div>
          )}
        </div>
      </div>
    );
  }

  const DetailPanel = () => {
    if (!selectedDispute) {
      return (
        <div className="hidden lg:flex flex-col items-center justify-center h-full text-center py-24 px-8">
          <Scale size={48} className="text-gray-200 mb-4" />
          <p className="text-gray-400">{t('admin.selectDispute')}</p>
        </div>
      );
    }
    const d = selectedDispute;
    return (
      <div className="overflow-y-auto h-full px-1">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-[#134168]">
              {t('admin.disputeNum', { n: d.id?.slice(-6) })}
            </h3>
            <p className="text-sm text-gray-500">{d.booking_code}</p>
          </div>
          <StatusBadge status={d.status} />
        </div>

        <div className="space-y-3 mb-4">
          <InfoRow icon={<Clock size={15} className="text-[#39b4aa]" />} label={t('admin.bookingDate')} value={fmt(d.created_at)} />
          <InfoRow
            icon={<User size={15} className="text-[#39b4aa]" />}
            label={t('admin.customer')}
            value={`${d.client_name || '—'} — ${d.client_email || '—'}`}
          />
          <InfoRow
            icon={<User size={15} className="text-[#134168]" />}
            label={t('admin.provider')}
            value={`${d.provider_name || '—'} — ${d.provider_email || '—'}`}
          />
          <InfoRow
            icon={<MessageSquare size={15} className="text-gray-400" />}
            label={t('admin.reason')}
            value={d.reason}
            multiline
          />
          <InfoRow
            icon={<DollarSign size={15} className="text-gray-400" />}
            label={t('admin.totalAmount')}
            value={money(d.total_price)}
          />
          <InfoRow
            icon={
              d.status === 'open' ? (
                <AlertCircle size={15} className="text-red-500" />
              ) : (
                <CheckCircle size={15} className="text-gray-400" />
              )
            }
            label={t('admin.currentStatus')}
            value={<StatusBadge status={d.status} />}
          />
        </div>

        {d.status === 'open' ? (
          <ResolutionSection d={d} />
        ) : (
          <ResolvedSummary d={d} />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <AdminTabs />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-10">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="mb-8 flex items-center gap-2 text-gray-500 hover:text-[#134168] font-medium transition-colors"
        >
          {t('admin.backToPanelShort')}
        </button>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-heading font-bold text-[#134168]">
            {t('admin.disputesTitle')}
          </h1>
          <button
            onClick={() => fetchDisputes(statusFilter)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#134168] transition-colors"
          >
            <RefreshCw size={15} />
            {t('admin.refresh')}
          </button>
        </div>

        {error && !selectedDispute && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6">
            {error}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-280px)]">
          <div className="lg:w-96 shrink-0 flex flex-col">
            <div className="flex bg-white rounded-2xl shadow-sm p-1 mb-4">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setStatusFilter(tab.key);
                    setSelectedDispute(null);
                  }}
                  className={`flex-1 py-2 text-sm font-semibold rounded-xl transition-colors ${
                    statusFilter === tab.key
                      ? 'bg-[#134168] text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto pr-1">
              {loading ? (
                <AdminLoadingScreen chrome={false} className="pt-12" message={t('admin.loadingDisputes', 'Cargando disputas...')} />
              ) : disputes.length === 0 ? (
                <EmptyState />
              ) : (
                disputes.map((d) => <DisputeCard key={d.id} d={d} />)
              )}
            </div>
          </div>

          <div className="flex-1 bg-white rounded-3xl shadow-sm p-8 overflow-hidden flex flex-col">
            <DetailPanel />
          </div>
        </div>
      </div>
    </div>
  );
}
